<template>
    <header
        v-dark
        class="head"
        :class="{
            'head--center': !user && !showMinimalHeader
        }"
    >
        <SkipToContent />
        <AssignmentsSidePanel
            v-if="!isLoading && showAssignmentsSidePanel"
            key="head"
            :assignments="assignments"
            :current-exam="currentExamMetadata"
            @close="showAssignmentsSidePanel = false"
        />
        <MobileMenu
            v-if="
                showMobileMenu
                    && !isUpgradeModalVisible
                    && sortedExams
                    && breakpoint === 'black-bear'
                    && currentExamMetadata
            "
            :exam-metadata="sortedExams"
            :current-exam-metadata="currentExamMetadata"
            @openUpgradeModal="openUpgradeModal"
            @close="closeMobileMenu()"
        />
        <ExamMenu
            v-if="user && currentExamMetadata"
            class="head__exam-menu"
            :exam-metadata="sortedExams"
            :current-exam-metadata="currentExamMetadata"
        />
        <div class="head__logo" v-if="showMinimalHeader">
            <RouterLink
                :to="{ name: 'sign-in' }"
            >
                <img
                    src="@/assets/logos/logo-alt.svg"
                    alt="Pocket Prep Logo"
                >
            </RouterLink>
        </div>
        <div class="head__exam-mobile">
            <BundleIcon
                v-if="bundle"
                theme="silver"
                :bundle-id="bundle.objectId"
            />
            <div class="head__exam-mobile-name">
                {{ currentExamMetadata && currentExamMetadata.nativeAppName }}
            </div>
        </div>
        <div
            ref="mobileMenuIcon"
            class="head__burger-menu"
            tabindex="0"
            role="button"
            aria-label="Open Menu"
            :aria-hidden="showMobileMenu"
            v-if="!showMinimalHeader"
            @click="openMobileMenu"
            @keydown.enter="openMobileMenu"
            @mousedown.prevent
        >
            <img src="@/assets/mobile/burger.svg" alt="menu">
        </div>
        <nav class="head__navigation"
             role="tablist"
             v-if="!showMinimalHeader"
        >
            <RouterLink
                v-slot="{ isActive, navigate, href }"
                custom
                :to="{ name: 'study' }"
            >
                <a
                    v-dark
                    :href="href"
                    role="tab"
                    :aria-selected="isActive ? 'true' : 'false'"
                    class="head__navigation-link"
                    :class="{ 'head__navigation-link--active': isActive }"
                    :tabindex="user ? '0' : '-1'"
                    @click="navigate"
                    @mousedown.prevent
                >
                    Study
                </a>
            </RouterLink>
            <RouterLink
                v-slot="{ isActive, navigate, href }"
                custom
                :to="{ name: 'stats' }"
            >
                <a
                    v-dark
                    :href="href"
                    role="tab"
                    :aria-selected="isActive ? 'true' : 'false'"
                    class="head__navigation-link"
                    :class="{ 'head__navigation-link--active': isActive }"
                    :tabindex="user ? '0' : '-1'"
                    @click="navigate"
                    @mousedown.prevent
                >
                    Stats
                </a>
            </RouterLink>
            <RouterLink
                v-slot="{ isActive, navigate, href }"
                custom
                :to="{ name: 'review' }"
            >
                <a
                    v-dark
                    :href="href"
                    role="tab"
                    :aria-selected="isActive ? 'true' : 'false'"
                    class="head__navigation-link"
                    :class="{ 'head__navigation-link--active': isActive }"
                    :tabindex="user ? '0' : '-1'"
                    @click="navigate"
                    @mousedown.prevent
                >
                    Review
                </a>
            </RouterLink>
            <RouterLink
                v-if="showNotebookExperiment && user"
                v-slot="{ isActive, navigate, href }"
                custom
                :to="{ name: 'notebook' }"
            >
                <a
                    v-dark
                    :href="href"
                    role="tab"
                    :aria-selected="isActive ? 'true' : 'false'"
                    class="head__navigation-link head__navigation-link--saved"
                    :class="{ 'head__navigation-link--active': isActive }"
                    :tabindex="user ? '0' : '-1'"
                    @click="navigate"
                    @mousedown.prevent
                >
                    <div>Saved</div>
                    <Tag size="small"
                         :isBetaTag="true"
                         class="head__navigation-link-beta">
                        <template #tagMessage>
                            Beta
                        </template>
                    </Tag>
                </a>
            </RouterLink>
        </nav>
        <PocketButton
            v-if="!isLoading && !hasActiveSubscription && user && currentExamMetadata && !isDisabledBundle"
            class="head__upgrade"
            type="primary-yellow"
            :is-dark-mode="isDarkMode"
            @click="openUpgradeModal"
        >
            Upgrade to Premium
        </PocketButton>
        <PocketButton
            v-else-if="!isLoading && hasActiveSubscription && user && currentExamMetadata && areAssignmentsEnabled"
            class="head__assignments"
            type="oval-dark"
            :is-dark-mode="isDarkMode"
            @click="openAssignmentSidePanel"
        >
            {{ assignments.length ?
                `${assignments.length} Assignment${assignments.length > 1 ? 's' : ''}` : 'Assignments'
            }}
        </PocketButton>
        <div v-if="user" class="head__right">
            <UserMenu />
            <div
                v-dark
                class="head__right-help"
                :tabindex="user ? '0' : '-1'"
                role="button"
                @click="showIntercom"
                @keydown.enter="showIntercom"
                @mousedown.prevent
            >
                Help
            </div>
        </div>
    </header>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import UserMenu from '@/components/Head/UserMenu.vue'
import ExamMenu from '@/components/Head/ExamMenu.vue'
import MobileMenu from '@/components/Head/MobileMenu.vue'
import SkipToContent from '@/components/SkipToContent.vue'
import AssignmentsSidePanel from '@/components/Assignments/AssignmentsSidePanel.vue'
import { userModule } from '@/store/user/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { bundleModule } from '@/store/bundle/module'
import { screenModule } from '@/store/screen/module'
import { referralModule } from '@/store/referral/module'
import { subscriptionModule } from '@/store/subscription/module'
import { assignmentModule } from '@/store/assignment/module'
import { analyticsModule } from '@/store/analytics/module'
import { studentModule } from '@/store/student/module'
import type { Teach } from '@pocketprep/types'
import { isSupportWindow } from '@/utils'
import { upgradeModule } from '@/store/upgrade/module'
import { giftModule } from '@/store/gift/module'
import { notebookModule } from '@/store/notebook/module'

const {
    BundleIcon,
    Button: PocketButton,
    Tag,
} = UIKit

const route = useRoute()

const isLoading = ref(true)
const showMobileMenu = ref(false)
const showAssignmentsSidePanel = ref(false)
const clientVersion = import.meta.env.VUE_APP_VERSION
const mobileMenuIcon = ref<HTMLElement | null>(null)

const showNotebookExperiment = computed(() => {
    if (notebookModule.state.experimentEndDate < new Date()) {
        return false
    }

    return !!notebookModule.state.notebookItems.length
})

const showMinimalHeader = computed(() => {
    if (route.name === 'ssoError') {
        return true
    }

    return false
})

const user = computed(() => {
    return userModule.state.user
})

const userExamMetadata = computed(() => {
    return userExamMetadataModule.getters.getUserExamMetadata()
})

const currentExamMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const examMetadataById = computed(() => {
    return examMetadataModule.getters.getExamMetadataById()
})

const examMetadata = computed(() => {
    const stateExamMetadata = examMetadataModule.getters.getExamMetadata()

    return userExamMetadata.value.map(uem => {
        return stateExamMetadata.find(
            e => e.version.startsWith(`${uem.examVersion?.split('.')[0]}.`) && e.examGuid === uem.examGuid
        ) || examMetadataModule.getters.getMostRecentExamForExamGuid(uem.examGuid)
    }).filter(e => e)
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const sortedExams = computed(() => {
    return [
        currentExamMetadata.value,
        ...[ ...examMetadata.value ]
            .filter(e => e?.examGuid !== currentExamMetadata.value?.examGuid)
            .sort((a, b) => a.nativeAppName.localeCompare(b.nativeAppName)),
    ].filter(sortExamVals => sortExamVals !== null)
})

const bundle = computed(() => {
    return bundleModule.getters.getBundles()
        .find(b => b.exams.find(e => {
            const bundleExam = examMetadataById.value[e.objectId]
            return currentExamMetadata?.value && bundleExam?.examGuid === currentExamMetadata?.value.examGuid
        }))
})

const hasActiveSubscription = computed(() => {
    return subscriptionModule.getters.getSubscriptionForExamId()
})

const validReferral = computed(() => {
    return referralModule.getters.getValidReferral()
})

const referralError = computed(() => {
    return referralModule.getters.getReferralError()
})

const hasSeenReferralUpgradePanel = computed(() => {
    return referralModule.state.hasSeenReferralUpgradePanel
})

const validGift = computed(() => {
    return giftModule.getters.getValidGift()
})

const giftError = computed(() => {
    return giftModule.getters.getGiftError()
})

const hasSeenRedeemGiftNotification = computed(() => {
    return giftModule.state.hasSeenRedeemGiftNotification
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const areAssignmentsEnabled = computed(() => {
    // assignments are enabled when an org has created at least one assignment
    return assignmentModule.getters.getAreAssignmentsEnabled()
})

const assignments = computed(() => {
    return assignmentModule.getters.getSortedAvailableAssignments()
})

const studentOrgs = computed(() => {
    return studentModule.getters.getStudentOrgs()
})

const isUpgradeModalVisible = computed(() => {
    return upgradeModule.getters.getIsShowingUpgradeModal()
})

const isDisabledBundle = computed(() => {
    return bundleModule.getters.isBundleDisabled(bundle.value?.name)
})

onMounted(async () => {
    if (showMinimalHeader.value) {
        isLoading.value = false
        return
    }

    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
        bundleModule.actions.fetchBundles(),
        userModule.actions.fetchUserData(),
        referralModule.actions.validateReferral(),
        giftModule.actions.validateGift(),
    ])

    validateShowUpgradeModalState()
    validateShowRedeemGiftModalState()

    isLoading.value = false
})

const referralAdded = (newVal: unknown, oldVal: unknown) => {
    if (!oldVal && newVal) {
        validateShowUpgradeModalState()
    }
}

const giftAdded = (newVal: unknown, oldVal: unknown) => {
    if (!oldVal && newVal) {
        validateShowRedeemGiftModalState()
    }
}

const routeChanged = () => {
    validateShowUpgradeModalState()
    validateShowRedeemGiftModalState()
}

const validateShowUpgradeModalState = () => {
    if (!hasSeenReferralUpgradePanel.value
        && (validReferral.value || referralError.value)
        && currentExamMetadata.value
        && user.value
        && route.name !== 'register'
    ) {
        const shouldOpenUpgradeModal = hasActiveSubscription.value ? false : true
        if (shouldOpenUpgradeModal) {
            openUpgradeModal()
        }
    }
}

const validateShowRedeemGiftModalState = () => {
    if (!hasSeenRedeemGiftNotification.value
        && (validGift.value || giftError.value)
        && currentExamMetadata.value
        && user.value
        && route.name !== 'register'
    ) {
        openRedeemGiftModal()
    }
}

const showIntercom = () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('show')
    }
}

const openMobileMenu = () => {
    showMobileMenu.value = true
}

const closeMobileMenu = () => {
    showMobileMenu.value = false

    nextTick(() => {
        mobileMenuIcon.value?.focus()
    })
}

const openUpgradeModal = () => {
    upgradeModule.actions.openUpgradeModal()
    showMobileMenu.value = false
}

const openRedeemGiftModal = () => {
    giftModule.actions.openRedeemGiftModal()
}

const openAssignmentSidePanel = () => {
    showAssignmentsSidePanel.value = true
    let orgId = ''

    if (assignments.value.length) {
        const assignmentOrg = assignments.value[0].organization as Teach.Class.OrganizationJSON
        orgId = assignmentOrg?.objectId
    } else {
        orgId = studentOrgs.value[0]?.objectId
    }

    analyticsModule.actions.amplitudeTrack('Button Clicked', {
        client: 'Web Study',
        clientAppId: 'study.pocketprep.com',
        clientVersion,
        callToAction: 'Assignments',
        containerName: 'Header',
        screenName: analyticsModule.getters.getScreenName(route),
        examGuid: currentExamMetadata.value?.examGuid || '',
        bundleId: bundle.value?.objectId || '',
        organizationId: orgId,
    })
}

watch(validReferral, referralAdded)
watch(referralError, referralAdded)
watch(validGift, giftAdded)
watch(giftError, giftAdded)
watch(() => route.fullPath, routeChanged)
</script>

<style lang="scss" scoped>
.head {
    height: 54px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-content: center;
    padding: 0 46px 0 34px;
    align-items: center;
    box-sizing: border-box;
    background: $tangaroa;
    color: $white;

    @include breakpoint(grizzly-bear) {
        padding: 0 13px 0 6px;
    }

    @include breakpoint(black-bear) {
        padding: 0;
        position: initial;
    }

    &--dark {
        background: $charcoal;
    }

    &--center {
        justify-content: center;
    }

    &__exam-menu {
        width: 320px;
        position: relative;
        z-index: 1001; // just above side panel

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__logo {
        margin-left: 44px;

        img {
            width: 140px;
            display: block;
        }
    }

    &__exam-mobile {
        display: none;
        align-items: center;
        margin-left: 4px;
        width: calc(100% - 48px);

        @include breakpoint(black-bear) {
            display: flex;
        }

        .uikit-bundle-icon {
            width: 47px;
            height: 47px;
            margin-right: 8px;
        }
    }

    &__exam-mobile-name {
        font-size: 18px;
        line-height: 25px;
        font-weight: 600;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        max-width: 240px;
        width: 100%;
    }

    &__burger-menu {
        display: none;
        width: 44px;
        height: 44px;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        user-select: none;
        margin-right: 4px;

        @include breakpoint(black-bear) {
            display: flex;
        }
    }

    &__upgrade {
        position: absolute !important;
        right: 322px;
        top: 9px;
        padding: 9px 18px 8px;

        @include breakpoint(grizzly-bear) {
            right: 225px;
        }

        @include breakpoint(brown-bear) {
            display: none;
        }
    }

    &__assignments {
        position: absolute !important;
        right: 322px;

        @include breakpoint(grizzly-bear) {
            right: 225px;
        }

        @include breakpoint(brown-bear) {
            display: none;
        }
    }

    &__navigation {
        display: flex;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__navigation-link {
        color: $white;
        opacity: 0.8;
        text-decoration: none;
        padding: 18px 2px;
        position: relative;
        font-size: 15px;
        letter-spacing: 0.3px;
        line-height: 18px;
        font-weight: 600;
        display: block;
        margin: 0 7px;
        outline: none;

        &--saved {
            display: flex;
        }

        &:hover {
            color: $banana-bread;
            opacity: 1;
        }

        &:focus {
            color: $banana-bread;
            opacity: 1;

            &::after {
                content: '';
                left: -6px;
                top: 4px;
                border: 1px solid $banana-bread;
                width: calc(100% + 9px);
                height: 44px;
                border-radius: 6px;
                position: absolute;
            }
        }

        &--active {
            opacity: 1;
            color: $banana-bread;

            &::before {
                content: '';
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 4px;
                background: $banana-bread;
            }
        }
    }

    &__navigation-link-beta {
        margin-left: 9px;
    }

    &__left {
        width: 320px;
    }

    &__right {
        display: flex;
        align-items: center;
        width: 306px;
        justify-content: flex-end;

        @include breakpoint(brown-bear) {
            width: 268px;
        }

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__right-help {
        color: $white;
        text-decoration: none;
        padding: 10px 8px 11px;
        margin-left: 10px;
        display: block;
        font-size: 15px;
        position: relative;
        outline: none;
        border-radius: 6px;
        cursor: pointer;

        &:hover {
            background: $charcoal;
        }

        &:focus {
            background: $charcoal;

            &::before {
                content: '';
                left: 0;
                top: 0;
                position: absolute;
                border: 1px solid $banana-bread;
                width: calc(100% - 2px);
                border-radius: 6px;
                height: 36px;
            }
        }

        &--dark {
            &:hover,
            &:focus {
                background: $brand-black;
            }
        }
    }
}
</style>
